import { useEffect, useRef, useState } from "react";

import { Package, Plus } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  useActivePallet,
  useBatchPallets,
  useDeletePallet,
  useEndPalletProduction,
  useMarkStickerPrinted,
  useStartPalletProduction,
  useUpdatePallet,
} from "~/hooks/production";
import { useCurrentUser } from "~/hooks/useCurrentUser";
import { useToast } from "~/hooks/use-toast";
import type { ProductionBatch } from "~/types/production";
import type { BatchPallet } from "~/types/wms";

import { ActivePalletCard } from "./ActivePalletCard";
import { PalletLabelPrint } from "./PalletLabelPrint";
import { PalletTable } from "./PalletTable";

// Standard boxes per pallet operators confirm at finalize for boxed/unit
// products; they can override per pallet. Bulk containers prefill by container:
// KGS octobins/tambores by weight (weight_kg), LTS totems by liter capacity.
const DEFAULT_BOXES_PER_PALLET = 36;

interface PalletProductionSectionProps {
  batch: ProductionBatch;
}

export function PalletProductionSection({
  batch,
}: PalletProductionSectionProps) {
  const { toast } = useToast();
  const { isAdmin } = useCurrentUser();
  const [palletToPrint, setPalletToPrint] = useState<BatchPallet | null>(null);
  const [palletToDelete, setPalletToDelete] = useState<string | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  // Queries
  const { data: pallets = [], isLoading: palletsLoading } = useBatchPallets(
    batch.id
  );
  const { data: activePallet } = useActivePallet(batch.id);

  // Mutations
  const startMutation = useStartPalletProduction();
  const endMutation = useEndPalletProduction();
  const printMutation = useMarkStickerPrinted();
  const updateMutation = useUpdatePallet();
  const deleteMutation = useDeletePallet();

  const handleStartPallet = async () => {
    try {
      const pallet = await startMutation.mutateAsync(batch.id);
      toast({
        title: "Pallet iniciado",
        description: `Pallet #${pallet.sequence_number} en producción`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo iniciar el pallet",
        variant: "destructive",
      });
    }
  };

  const handleEndPallet = async (palletId: string, boxCount: number) => {
    try {
      const pallet = await endMutation.mutateAsync({ palletId, boxCount });
      toast({
        title: "Pallet completado",
        description: `Pallet #${pallet.sequence_number} finalizado con ${boxCount} cajas`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo finalizar el pallet",
        variant: "destructive",
      });
    }
  };

  const handleUpdateBoxCount = async (palletId: string, boxCount: number) => {
    try {
      await updateMutation.mutateAsync({ palletId, boxCount });
      toast({
        title: "Pallet actualizado",
        description: `Cantidad actualizada a ${boxCount} cajas`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo actualizar el pallet",
        variant: "destructive",
      });
    }
  };

  const handleDeletePallet = (palletId: string) => {
    setPalletToDelete(palletId);
  };

  const confirmDeletePallet = async () => {
    if (!palletToDelete) return;

    try {
      await deleteMutation.mutateAsync({
        palletId: palletToDelete,
        batchId: batch.id,
      });
      toast({
        title: "Pallet eliminado",
        description: "El pallet ha sido eliminado correctamente",
      });
      setPalletToDelete(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar el pallet",
        variant: "destructive",
      });
    }
  };

  // Direct print: when palletToPrint is set, wait for render then open print window
  useEffect(() => {
    if (!palletToPrint) return;

    // Small delay to ensure Barcode SVG renders
    const timer = setTimeout(() => {
      if (!printRef.current) return;

      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        toast({
          title: "Error",
          description: "Permite ventanas emergentes para imprimir etiquetas",
          variant: "destructive",
        });
        setPalletToPrint(null);
        return;
      }

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Etiqueta Pallet - ${palletToPrint.code}</title>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              html, body { width: 100%; height: 100%; margin: 0; padding: 0; overflow: hidden; font-family: system-ui, -apple-system, sans-serif; }
            </style>
          </head>
          <body>${printRef.current.innerHTML}</body>
        </html>
      `);
      printWindow.document.close();

      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };

      // Mark as printed
      printMutation
        .mutateAsync(palletToPrint.id)
        .then(() => {
          toast({
            title: "Etiqueta impresa",
            description: `Pallet #${palletToPrint.sequence_number} marcado como impreso`,
          });
        })
        .catch((error) => {
          console.error("Error marking sticker as printed:", error);
        });

      setPalletToPrint(null);
    }, 150);

    return () => clearTimeout(timer);
  }, [palletToPrint]);

  const handlePrintSticker = (pallet: BatchPallet) => {
    setPalletToPrint(pallet);
  };

  const canStartNewPallet =
    batch.status === "in_progress" || batch.status === "planned";
  const hasActivePallet = !!activePallet;

  // Pre-fill the finalize quantity so operators confirm instead of typing.
  // The prefill must be expressed IN the production unit, one pallet == one container:
  // - KGS octobins/tambores: weight_kg (kilos per container).
  // - LTS totems: the liter capacity (volume_cm3 / 1000), NOT weight_kg — weight_kg is
  //   kilos, so prefilling it under a "litros" label prints the wrong number (see gotcha
  //   pallet-tarja-quantity-frozen-at-print).
  // - box/unit products: the standard boxes-per-pallet default.
  let expectedPalletQuantity: number | undefined;
  if (batch.production_unit === "KGS") {
    const weightKg = Number(batch.product?.weight_kg ?? 0);
    expectedPalletQuantity = weightKg > 0 ? weightKg : undefined;
  } else if (batch.production_unit === "LTS") {
    const volumeCm3 = Number(batch.product?.volume_cm3 ?? 0);
    expectedPalletQuantity = volumeCm3 > 0 ? volumeCm3 / 1000 : undefined;
  } else {
    expectedPalletQuantity = DEFAULT_BOXES_PER_PALLET;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Pallets
          {batch.lot_number && (
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              Lote: {batch.lot_number}
            </span>
          )}
        </CardTitle>
        {canStartNewPallet && !hasActivePallet && (
          <Button
            onClick={handleStartPallet}
            disabled={startMutation.isPending}
          >
            <Plus className="mr-2 h-4 w-4" />
            {startMutation.isPending ? "Iniciando..." : "Iniciar Pallet"}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Active pallet card */}
        {activePallet && (
          <ActivePalletCard
            key={activePallet.id}
            pallet={activePallet}
            onEndProduction={handleEndPallet}
            isEnding={endMutation.isPending}
            productionUnit={batch.production_unit}
            expectedQuantity={expectedPalletQuantity}
            onDiscard={handleDeletePallet}
          />
        )}

        {/* Pallets table */}
        {palletsLoading ? (
          <div className="py-8 text-center text-muted-foreground">
            Cargando pallets...
          </div>
        ) : (
          <PalletTable
            pallets={pallets}
            onPrintSticker={handlePrintSticker}
            onUpdateBoxCount={
              canStartNewPallet ? handleUpdateBoxCount : undefined
            }
            onDeletePallet={handleDeletePallet}
            showDeleteButton={isAdmin}
            productionUnit={batch.production_unit}
          />
        )}

        {/* After completing a pallet, show option to start next */}
        {!hasActivePallet && pallets.length > 0 && canStartNewPallet && (
          <div className="flex justify-center border-t pt-4">
            <Button
              variant="outline"
              onClick={handleStartPallet}
              disabled={startMutation.isPending}
            >
              <Plus className="mr-2 h-4 w-4" />
              Iniciar Siguiente Pallet
            </Button>
          </div>
        )}
      </CardContent>

      {/* Hidden label for direct printing */}
      {palletToPrint && (
        <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
          <PalletLabelPrint
            ref={printRef}
            pallet={palletToPrint}
            batch={batch}
          />
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={!!palletToDelete}
        onOpenChange={(open) => !open && setPalletToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Pallet</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro de que desea eliminar este pallet? Esta acción no se
              puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeletePallet}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Eliminando..." : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
